import * as THREE from 'three';
import { easeOutCubic } from '../../utils/easings';
import type { Shockwave, ShockwaveType } from '../../state/game-state';

const POOL: Shockwave[] = [];

const ringGeo = new THREE.RingGeometry(0.99, 1.0, 64);

function makeWaveMaterial(color: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.85,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
}

const mats: Record<ShockwaveType, THREE.MeshBasicMaterial> = {
  wind: makeWaveMaterial(0xfca),
  impact: makeWaveMaterial(0xfb6),
  shield: makeWaveMaterial(0xffd),
};

export class ShockwaveSystem {
  public root: THREE.Group;
  private meshes = new Map<string, THREE.Mesh>();
  private dying = new Set<string>();

  constructor() {
    this.root = new THREE.Group();
  }

  spawn(
    type: ShockwaveType,
    origin: THREE.Vector3,
    durationMs: number,
    maxRadius: number,
    color: number,
    knockback: number
  ): Shockwave {
    const id = `sw-${POOL.length}-${performance.now()}`;
    const wave: Shockwave = {
      id,
      type,
      startedAt: performance.now(),
      durationMs,
      maxRadius,
      color,
      knockback,
    };
    POOL.push(wave);
    const mat = mats[type].clone();
    const mesh = new THREE.Mesh(ringGeo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(origin.x, 0.05, origin.z);
    mesh.scale.set(0.01, 0.01, 0.01);
    this.meshes.set(id, mesh);
    this.root.add(mesh);
    if (type === 'shield') {
      mesh.position.y = origin.y;
    }
    return wave;
  }

  activeWaves(): Shockwave[] {
    const now = performance.now();
    return POOL.filter((w) => now - w.startedAt < w.durationMs);
  }

  update(now: number): void {
    for (let i = POOL.length - 1; i >= 0; i--) {
      const w = POOL[i];
      const age = now - w.startedAt;
      if (age >= w.durationMs) {
        const mesh = this.meshes.get(w.id);
        if (mesh) {
          this.root.remove(mesh);
          (mesh.material as THREE.Material).dispose();
          this.meshes.delete(w.id);
        }
        POOL.splice(i, 1);
        continue;
      }
      const t = easeOutCubic(age / w.durationMs);
      const mesh = this.meshes.get(w.id);
      if (!mesh) continue;
      const r = Math.max(0.1, w.maxRadius * t);
      mesh.scale.set(r, r, r);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      if (w.type === 'shield') {
        mesh.scale.set(r, 1, r);
        mat.opacity = 0.5 + 0.3 * Math.sin(age * 0.01);
      } else {
        mat.opacity = 0.85 * (1 - t);
      }
    }
  }
}
